import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { QrCodeScanner } from '@mui/icons-material';
// @ts-ignore
import jsQR from 'jsqr';

interface QRCodeScannerProps {
  onScan: (data: string) => void;
  onError: (error: any) => void;
  onClose: () => void;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({ onScan, onError, onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    startScanning();
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      console.log('📹 開始請求攝像頭權限...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      console.log('📹 攝像頭權限獲取成功');
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
        console.log('📹 視頻流已設置，等待視頻準備就緒...');
        
        // 等待視頻準備就緒後開始掃描循環
        videoRef.current.onloadedmetadata = () => {
          console.log('📹 視頻準備就緒，開始掃描循環');
          console.log('📹 視頻尺寸:', {
            videoWidth: videoRef.current?.videoWidth,
            videoHeight: videoRef.current?.videoHeight
          });
          scanLoop();
        };
      } else {
        console.error('📹 videoRef.current 為空');
      }
    } catch (err) {
      console.error('📹 攝像頭訪問失敗:', err);
      setError('無法訪問攝像頭');
      onError(err);
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  const scanLoop = () => {
    if (!isScanning || !videoRef.current || !canvasRef.current) {
      console.log('🔍 掃描循環停止：', { isScanning, hasVideo: !!videoRef.current, hasCanvas: !!canvasRef.current });
      return;
    }

    // 每1000次掃描輸出一次狀態信息
    if (Math.random() < 0.001) {
      console.log('🔍 掃描循環運行中...', {
        isScanning,
        videoReadyState: videoRef.current?.readyState,
        videoWidth: videoRef.current?.videoWidth,
        videoHeight: videoRef.current?.videoHeight
      });
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('🔍 無法獲取canvas context');
      return;
    }

    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      // 繼續掃描循環
      if (isScanning) {
        requestAnimationFrame(scanLoop);
      }
      return;
    }

    // 設置canvas尺寸
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // 繪製視頻幀到canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // 獲取圖像數據
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // 使用jsQR進行QR Code解碼
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert",
    });

    // 每100次掃描輸出一次調試信息
    if (Math.random() < 0.01) {
      console.log('🔍 掃描調試信息:', {
        imageDataSize: imageData.data.length,
        imageWidth: imageData.width,
        imageHeight: imageData.height,
        hasCode: !!code,
        codeData: code?.data
      });
    }

    if (code) {
      console.log('🔍 掃描到QR Code:', code.data);
      console.log('🔍 QR Code 詳情:', {
        data: code.data,
        location: code.location,
        version: code.version
      });
      console.log('🔍 調用 onScan 函數...');
      setIsScanning(false); // 停止掃描
      try {
        onScan(code.data);
        console.log('🔍 onScan 函數調用成功');
      } catch (error) {
        console.error('🔍 onScan 函數調用失敗:', error);
      }
      return; // 掃描成功，停止循環
    }

    // 繼續掃描循環
    if (isScanning) {
      requestAnimationFrame(scanLoop);
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Typography variant="h6">掃描QR Code</Typography>
      
      {error ? (
        <Box textAlign="center">
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="outlined" onClick={onClose}>
            關閉
          </Button>
        </Box>
      ) : (
        <>
          <Box position="relative" border="2px solid #ccc" borderRadius={2} overflow="hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '300px', height: '300px' }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            
            {/* 掃描框指示器 */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200px',
                height: '200px',
                border: '2px solid #00ff00',
                borderRadius: 1
              }}
            />
          </Box>
          
          <Typography variant="body2" color="textSecondary" textAlign="center">
            將QR Code對準掃描框
          </Typography>
          
          {/* 調試信息 */}
          <Typography variant="caption" color="textSecondary">
            掃描狀態: {isScanning ? '掃描中' : '已停止'}
          </Typography>
          
          {/* 測試按鈕 */}
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => {
              console.log('🧪 測試掃描 - 模擬QR Code數據');
              onScan('test-session-id-123');
            }}
          >
            測試掃描
          </Button>
          
          <Button variant="outlined" onClick={onClose}>
            取消掃描
          </Button>
        </>
      )}
    </Box>
  );
};

export default QRCodeScanner; 